import BaseController from "./base_controller"
import { t } from "@/utils/i18n"

// Connects to data-controller="settings"
export default class extends BaseController {
  static targets = ["tab", "tabPane", "clipboardInput", "copyButton", "reminderToggle", "reminderTime"]
  static values = { 
    defaultTab: { type: String, default: "profile" }
  }

  private readonly turboSubmitStartHandler = this.handleTurboSubmitStart.bind(this)
  private readonly turboSubmitEndHandler = this.handleTurboSubmitEnd.bind(this)
  
  declare readonly tabTargets: HTMLElement[]
  declare readonly tabPaneTargets: HTMLElement[]
  declare readonly clipboardInputTarget: HTMLInputElement
  declare readonly copyButtonTarget: HTMLElement
  declare readonly hasClipboardInputTarget: boolean
  declare readonly hasCopyButtonTarget: boolean
  declare readonly hasReminderToggleTarget: boolean
  declare readonly reminderToggleTarget: HTMLInputElement
  declare readonly reminderTimeTarget: HTMLElement
  declare defaultTabValue: string

  connect(): void {
    super.connect()
    this.element.classList.add("settings-enhanced")
    this.initializeTabs()
    this.toggleReminder()
    
    this.element.addEventListener('turbo:submit-start', this.turboSubmitStartHandler)
    this.element.addEventListener('turbo:submit-end', this.turboSubmitEndHandler)
  }

  disconnect(): void {
    super.disconnect()
    this.element.classList.remove("settings-enhanced")
    
    this.element.removeEventListener('turbo:submit-start', this.turboSubmitStartHandler)
    this.element.removeEventListener('turbo:submit-end', this.turboSubmitEndHandler)
  }

  navigateSection(event: Event): void {
    const select = event.currentTarget as HTMLSelectElement
    window.location.assign(select.value)
  }

  toggleReminder(): void {
    if (!this.hasReminderToggleTarget) return
    this.reminderTimeTarget.hidden = !this.reminderToggleTarget.checked
  }

  initializeTabs(): void {
    // Ensure at least one tab is active
    const activeTab = this.tabTargets.find((tab: HTMLElement) => tab.classList.contains('tab-active'))
    
    if (!activeTab && this.tabTargets.length > 0) {
      // If no tab is active, activate the default one
      const defaultTab = this.tabTargets.find((tab: HTMLElement) => 
        tab.dataset.tab === this.defaultTabValue
      ) || this.tabTargets[0]
      
      this.activateTab(defaultTab)
    }
  }

  // Action method for tab switching
  switchTab(event: Event): void {
    event.preventDefault()
    
    const clickedTab = event.currentTarget as HTMLElement
    this.activateTab(clickedTab)
  }

  // Clipboard functionality
  copyToClipboard(event: Event): void {
    try {
      event.preventDefault()
      
      if (!this.hasClipboardInputTarget) {
        this.showErrorMessage(t('clipboard_input_not_found'))
        return
      }
      
      const input = this.clipboardInputTarget
      const originalButtonText = this.hasCopyButtonTarget ? this.copyButtonTarget.innerHTML : null
      
      // Select the input text
      input.select()
      input.setSelectionRange(0, 99999) // For mobile devices
      
      // Try modern Clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(input.value).then(() => {
          this.showCopySuccess(originalButtonText)
        }).catch(() => {
          // Fallback to execCommand
          this.fallbackCopyToClipboard(input, originalButtonText)
        })
      } else {
        // Fallback for older browsers or non-secure contexts
        this.fallbackCopyToClipboard(input, originalButtonText)
      }
      
    } catch (error) {
      this.handleError(error, 'copyToClipboard')
      this.showErrorMessage(t('copy_failed'))
    }
  }

  // Private methods for clipboard functionality
  private fallbackCopyToClipboard(_input: HTMLInputElement, originalButtonText: string | null): void {
    try {
      const successful = document.execCommand('copy')
      if (successful) {
        this.showCopySuccess(originalButtonText)
      } else {
        this.showErrorMessage(t('copy_failed'))
      }
    } catch (error) {
      this.handleError(error, 'fallbackCopyToClipboard')
      this.showErrorMessage(t('copy_not_supported'))
    }
  }

  private showCopySuccess(originalButtonText: string | null): void {
    if (this.hasCopyButtonTarget) {
      const button = this.copyButtonTarget
      
      // Update button appearance
      button.innerHTML = `✅ ${t('copied')}`
      button.classList.add('btn-success')
      button.classList.remove('btn-primary')
      
      // Reset after 2 seconds
      setTimeout(() => {
        if (originalButtonText) {
          button.innerHTML = originalButtonText
        }
        button.classList.remove('btn-success')
        button.classList.add('btn-primary')
      }, 2000)
    }
    
    this.showSuccessMessage(t('url_copied'))
  }

  // Private method to activate a specific tab
  private activateTab(tabElement: HTMLElement): void {
    const targetTabName = tabElement.dataset.tab
    
    if (!targetTabName) {
      this.debug("Warning: Tab element missing data-tab attribute", tabElement)
      return
    }
    
    // Remove active class from all tabs
    this.tabTargets.forEach((tab: HTMLElement) => {
      tab.classList.remove('tab-active')
    })
    
    // Add active class to clicked tab
    tabElement.classList.add('tab-active')
    
    // Hide all tab panes
    this.tabPaneTargets.forEach((pane: HTMLElement) => {
      pane.classList.remove('active')
    })
    
    // Show corresponding tab pane
    const targetPane = this.tabPaneTargets.find((pane: HTMLElement) => 
      pane.id === `${targetTabName}-tab`
    )
    
    if (targetPane) {
      targetPane.classList.add('active')
    } else {
      this.debug(`Warning: Tab pane not found for tab: ${targetTabName}`)
    }
  }

  // Helper to get current active tab name
  getActiveTabName(): string | null {
    const activeTab = this.tabTargets.find((tab: HTMLElement) => 
      tab.classList.contains('tab-active')
    )
    return activeTab ? (activeTab.dataset.tab || null) : null
  }

  // Programmatic tab switching (for external use)
  activateTabByName(tabName: string): void {
    const targetTab = this.tabTargets.find((tab: HTMLElement) => 
      tab.dataset.tab === tabName
    )
    
    if (targetTab) {
      this.activateTab(targetTab)
      this.showInfoMessage(t('switched_to_tab', { name: tabName }))
    } else {
      this.showWarningMessage(t('tab_not_found', { name: tabName }))
    }
  }

  // ✅ FÁZE 2: Progressive enhancement for iCal token regeneration
  regenerateToken(event: Event): void {
    const button = event.target as HTMLElement | null
    
    // Show loading state using base controller method
    this.showLoadingState(button, t('regenerating'))
    
    // Form submission handled by Turbo automatically
    // This is just UI enhancement
  }

  handleTurboSubmitStart(event: Event): void {
    const target = event.target as HTMLElement | null
    if (!target) return
    
    const button = target.querySelector<HTMLInputElement>('input[type="submit"]')
    if (button) {
      this.showLoadingState(button, t('regenerating'))
    }
  }

  handleTurboSubmitEnd(event: Event): void {
    const target = event.target as HTMLElement | null
    if (!target) return
    
    const button = target.querySelector<HTMLInputElement>('input[type="submit"]')
    if (button) {
      this.hideLoadingState(button)
    }
    
    const customEvent = event as CustomEvent
    if (customEvent.detail?.success) {
      // Success notification handled by Turbo Stream
    } else {
      this.debug("Token regeneration failed", { event })
      // Error handling through Rails/Turbo standard flow
    }
  }
}
